/**
 * ProjectHub — workspace projects landing surface.
 *
 * June 8, 2026 "feel alive" redesign — the page now mirrors the
 * ChatEmptyState narrative pattern so the user feels welcomed, not
 * filed: a time-aware greeting, the LoginBlob brand anchor, a bullet
 * list of what Ando noticed (overdue tasks, sleeping projects, fresh
 * artifacts), a primary smart CTA, and then the project grid.
 *
 * Cards moved from "icon + name + chat count" to a small story per row:
 * last conversation title, relative timestamp, live activity dot, and a
 * pair of compact chips (overdue / next-run / memory). The narrative
 * comes from the live BE fields on ``Project`` (last_conversation_*,
 * overdue_tasks_count, …) so the FE never has to fan out N+1 queries.
 *
 * Re-uses the same primitives the ChatEmptyState does (LoginBlob, motion
 * stagger, formatShortRelative) so the brand voice stays consistent
 * between the chat empty state and the project hub — they're the two
 * "first surface" landing pages the user meets at the start of every
 * session.
 */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Archive,
  ArrowRight,
  CalendarClock,
  ChevronDown,
  FilePlus2,
  FolderPlus,
  History,
  Loader2,
  Search,
  Sparkles,
  Timer,
} from "lucide-react";
import * as React from "react";

import type { Project } from "@ando/core/api";
import {
  Button,
  ListItemActionButton,
  ListItemRow,
  MetaItem,
  MetaRow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SurfaceLayout,
  cn,
  useReducedMotion,
} from "@ando/ui";
import { LoginBlob } from "@ando/ui/blob";
import { confirm } from "@ando/ui/dialog";

import { useChatUserId } from "@/features/chat";
import { useProfile } from "@/features/settings";

import { useArchiveProject, useProjects } from "../hooks/useProjects.js";
import { selectSetCurrentProjectId, useProjectsStore } from "../state/projectsStore.js";
import {
  ACTIVITY_DOT,
  classifyActivity,
  formatShortRelative,
  formatShortUntil,
} from "../utils/activity.js";
import { NewProjectDialog } from "./NewProjectDialog.js";
import { ProjectIcon } from "./ProjectIcon.js";
import { TemplatesBrowserDialog } from "./TemplatesBrowserDialog.js";

// ─── Greeting (time-aware) ────────────────────────────────────────────────
//
// Mirrors the ChatEmptyState bucket logic — same anchor words so the
// user hears the same voice across the two cold-start surfaces.
function pickGreeting(hour: number) {
  if (hour >= 5 && hour < 12) return msg`Buongiorno`;
  if (hour >= 12 && hour < 18) return msg`Buon pomeriggio`;
  if (hour >= 18 && hour < 24) return msg`Buonasera`;
  return msg`Notte bianca?`;
}

function firstNameOf(fullName: string | null | undefined): string {
  if (!fullName) return "";
  const trimmed = fullName.trim();
  if (trimmed.length === 0) return "";
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

// ─── Smart insight bullets ────────────────────────────────────────────────

interface ProjectInsight {
  readonly text: string;
  readonly project_id: string;
  readonly severity: "urgent" | "attention" | "info";
}

/**
 * Pick the 3 most narrative-worthy "things Ando noticed" across all
 * projects. Priority order:
 *   1. Projects with overdue tasks (urgent)
 *   2. Projects with an automation firing today (attention)
 *   3. Projects with fresh activity / unread memory (info)
 *
 * Falls back to a calm "tutto sotto controllo" copy when nothing
 * surfaces — same shape as ChatEmptyState's brief empty state.
 */
function pickInsights(projects: readonly Project[], i18n: I18n): readonly ProjectInsight[] {
  const out: ProjectInsight[] = [];
  // 1. Overdue tasks
  const overdue = [...projects]
    .filter((p) => (p.overdue_tasks_count ?? 0) > 0)
    .sort((a, b) => (b.overdue_tasks_count ?? 0) - (a.overdue_tasks_count ?? 0))
    .slice(0, 2);
  for (const p of overdue) {
    const count = p.overdue_tasks_count ?? 0;
    out.push({
      project_id: p.project_id,
      severity: "urgent",
      text:
        count === 1
          ? i18n._(msg`"${p.name}" ha 1 attività in ritardo`)
          : i18n._(msg`"${p.name}" ha ${count} attività in ritardo`),
    });
  }
  if (out.length >= 3) return out.slice(0, 3);

  // 2. Automation firing soon (within 48h, future only)
  const now = Date.now();
  const soon = [...projects]
    .filter((p) => !!p.next_run_at)
    .map((p) => ({ p, t: Date.parse(p.next_run_at!) }))
    .filter(({ t }) => !Number.isNaN(t) && t > now && t < now + 48 * 60 * 60 * 1000)
    .sort((a, b) => a.t - b.t)
    .slice(0, 2);
  for (const { p } of soon) {
    const when = formatShortUntil(p.next_run_at);
    out.push({
      project_id: p.project_id,
      severity: "attention",
      text: i18n._(msg`"${p.name}" — automazione ${when}`),
    });
    if (out.length >= 3) return out;
  }

  // 3. Recent activity / fresh artifact
  const fresh = [...projects]
    .filter((p) => !!p.last_active_at)
    .sort((a, b) => Date.parse(b.last_active_at!) - Date.parse(a.last_active_at!))
    .slice(0, 3);
  for (const p of fresh) {
    if (out.some((o) => o.project_id === p.project_id)) continue;
    const last = (p.last_conversation_title ?? "").trim();
    if (last.length > 0) {
      const when = formatShortRelative(p.last_active_at);
      out.push({
        project_id: p.project_id,
        severity: "info",
        text: i18n._(msg`"${p.name}" — ultima volta: "${last}", ${when}`),
      });
    } else if (p.last_artifact_filename) {
      const filename = p.last_artifact_filename;
      const when = formatShortRelative(p.last_artifact_at);
      out.push({
        project_id: p.project_id,
        severity: "info",
        text: i18n._(msg`"${p.name}" — ${filename}, ${when}`),
      });
    }
    if (out.length >= 3) break;
  }
  return out;
}

// ─── Component ───────────────────────────────────────────────────────────

const ENTER_TRANSITION = { duration: 0.22, ease: [0.32, 0.72, 0, 1] as const };

export const ProjectHub: React.FC = () => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const query = useProjects();
  const archiveMutation = useArchiveProject();
  const setCurrentProjectId = useProjectsStore(selectSetCurrentProjectId);
  const reducedMotion = useReducedMotion();

  // Profile → first name (mirrors ChatEmptyState).
  const userId = useChatUserId();
  const profile = useProfile(userId);
  const firstName = firstNameOf(profile.data?.name);
  const hour = React.useMemo(() => new Date().getHours(), []);
  const greeting = i18n._(pickGreeting(hour));

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const [newMenuOpen, setNewMenuOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const projects = React.useMemo<readonly Project[]>(
    () => (query.data ?? []).filter((p) => !p.is_archived),
    [query.data],
  );

  const filtered = React.useMemo<readonly Project[]>(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return projects;
    return projects.filter((p) => (p.name ?? "").toLowerCase().includes(needle));
  }, [projects, search]);

  const insights = React.useMemo(() => pickInsights(projects, i18n), [projects, i18n]);
  // The "Apri il primo da chiudere" CTA points at the most-overdue
  // project. Falls back to most-recent if nothing is overdue.
  const primaryCtaProject = React.useMemo<Project | null>(() => {
    const overdue = projects.find((p) => (p.overdue_tasks_count ?? 0) > 0);
    if (overdue) return overdue;
    const recents = [...projects]
      .filter((p) => !!p.last_active_at)
      .sort((a, b) => Date.parse(b.last_active_at!) - Date.parse(a.last_active_at!));
    return recents[0] ?? null;
  }, [projects]);

  const handleArchive = React.useCallback(
    async (project: Project) => {
      const confirmed = await confirm({
        title: i18n._(msg`Archiviare "${project.name}"?`),
        description: i18n._(
          msg`Il progetto verrà spostato in archivio. Le conversazioni resteranno nella cronologia, senza progetto associato.`,
        ),
        confirmLabel: i18n._(msg`Archivia`),
        destructive: true,
      });
      if (!confirmed) return;
      archiveMutation.mutate(project.project_id);
    },
    [archiveMutation, i18n],
  );

  const heroMotion = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: ENTER_TRANSITION,
      };

  const bulletMotion = (index: number): Record<string, unknown> => {
    if (reducedMotion) return {};
    return {
      initial: { opacity: 0, y: 6 },
      animate: { opacity: 1, y: 0 },
      transition: { ...ENTER_TRANSITION, delay: 0.05 + index * 0.04 },
    };
  };

  // The page is wrapped in SurfaceLayout but uses the legacy "no
  // SurfaceHeader" shape — the hero IS the header now. The actions
  // moved into a thin floating row above the grid.
  const showSearch = projects.length > 0;
  const isLoadingFresh = query.isLoading && projects.length === 0;

  return (
    <>
      <SurfaceLayout contentAriaLabel={i18n._(msg`I tuoi progetti`)}>
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 pt-8 pb-10">
          {/* ── Hero ────────────────────────────────────────────────── */}
          <motion.section
            {...heroMotion}
            aria-label={i18n._(msg`Saluto e attività progetti`)}
            className="flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <span aria-hidden className="block h-10 w-10 shrink-0">
                <LoginBlob size="mini" />
              </span>
              <span className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.08em]">
                <Trans>Ando · progetti</Trans>
              </span>
            </div>
            <h1 className="font-display font-semibold text-[28px] text-ink-1 leading-[1.15] tracking-[-0.012em]">
              {greeting}
              {firstName ? ` ${firstName}` : ""}.
            </h1>
            <HeroNarrative
              loading={isLoadingFresh}
              projectCount={projects.length}
              insights={insights}
            />
            <ul aria-live="polite" aria-busy={isLoadingFresh} className="flex flex-col gap-1.5">
              {isLoadingFresh
                ? [0, 1, 2].map((i) => (
                    <li
                      key={`skeleton-${i}`}
                      className="flex items-center gap-2 text-[13px] text-ink-2"
                    >
                      <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-ink-3/40" />
                      <span
                        aria-hidden
                        className="h-3 w-2/3 animate-pulse rounded-full bg-ink-3/15"
                      />
                    </li>
                  ))
                : insights.length > 0
                  ? insights.map((b, i) => (
                      <motion.li
                        key={`${b.project_id}-${i}`}
                        {...bulletMotion(i)}
                        className="flex items-start gap-2 text-[13px] text-ink-2"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "mt-[7px] h-1 w-1 shrink-0 rounded-full",
                            b.severity === "urgent"
                              ? "bg-ando-orange"
                              : b.severity === "attention"
                                ? "bg-amber-500"
                                : "bg-ink-3/50",
                          )}
                        />
                        <span className="min-w-0 flex-1">{b.text}</span>
                      </motion.li>
                    ))
                  : null}
            </ul>
            {/* CTA row — primary action when something needs attention,
                or "+ Nuovo" when the briefing is calm. */}
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {primaryCtaProject && insights.length > 0 ? (
                <Link
                  to="/workspace/projects/$projectId"
                  params={{ projectId: primaryCtaProject.project_id }}
                  onClick={() => setCurrentProjectId(primaryCtaProject.project_id)}
                  aria-label={i18n._(msg`Apri "${primaryCtaProject.name}"`)}
                  className={cn(
                    "inline-flex items-center gap-1.5 self-start rounded-full",
                    "border border-ando-orange/30 bg-ando-orange/10 px-3.5 py-1.5",
                    "font-medium text-[12.5px] text-ando-orange",
                    "transition-all hover:border-ando-orange/40 hover:bg-ando-orange/15",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                  )}
                >
                  <span>
                    <Trans>Apri "{primaryCtaProject.name}"</Trans>
                  </span>
                  <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
                </Link>
              ) : null}
              <NewProjectMenu
                open={newMenuOpen}
                onOpenChange={setNewMenuOpen}
                onNewBlank={() => {
                  setNewMenuOpen(false);
                  setDialogOpen(true);
                }}
                onNewFromTemplate={() => {
                  setNewMenuOpen(false);
                  setTemplatesOpen(true);
                }}
              />
            </div>
          </motion.section>

          {/* ── Search + divider ──────────────────────────────────── */}
          {showSearch ? (
            <div className="flex items-center gap-3">
              <span className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.08em]">
                <Trans>I tuoi spazi</Trans>
              </span>
              <span className="h-px flex-1 bg-hairline" aria-hidden />
              <div className="relative w-full max-w-[220px]">
                <Search
                  className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 text-ink-3"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={i18n._(msg`Cerca…`)}
                  aria-label={i18n._(msg`Cerca progetti`)}
                  className={cn(
                    "h-8 w-full rounded-md border border-hairline bg-transparent pr-3 pl-8",
                    "text-[12.5px] text-ink-1 placeholder:text-ink-3",
                    "focus-visible:border-ando-orange/45 focus-visible:outline-none",
                    "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
                  )}
                />
              </div>
            </div>
          ) : null}

          {/* ── Grid ──────────────────────────────────────────────── */}
          {isLoadingFresh ? (
            <ProjectHubSkeleton />
          ) : filtered.length === 0 && projects.length === 0 ? (
            <EmptyHero
              onTemplate={() => setTemplatesOpen(true)}
              onBlank={() => setDialogOpen(true)}
            />
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-hairline/60 border-dashed px-4 py-8 text-center text-[12.5px] text-ink-3">
              <Trans>Nessun progetto corrisponde alla ricerca. Prova con un altro termine.</Trans>
            </div>
          ) : (
            <ul className="flex flex-col" aria-label={i18n._(msg`I tuoi progetti`)}>
              {filtered.map((project, index) => (
                <ProjectRow
                  key={project.project_id}
                  project={project}
                  index={index}
                  onOpen={() => {
                    setCurrentProjectId(project.project_id);
                    void navigate({
                      to: "/workspace/projects/$projectId",
                      params: { projectId: project.project_id },
                    });
                  }}
                  onArchive={() => void handleArchive(project)}
                  archivePending={
                    archiveMutation.isPending && archiveMutation.variables === project.project_id
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </SurfaceLayout>
      <NewProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      <TemplatesBrowserDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </>
  );
};

ProjectHub.displayName = "ProjectHub";

// ───────────────────────────────────────────────────────────────────────────
// Hero narrative line — the "Ho 3 cose interessanti" copy
// ───────────────────────────────────────────────────────────────────────────

const HeroNarrative: React.FC<{
  readonly loading: boolean;
  readonly projectCount: number;
  readonly insights: readonly ProjectInsight[];
}> = ({ loading, projectCount, insights }) => {
  if (loading) {
    return (
      <p className="text-[14px] text-ink-2">
        <Trans>Sto sfogliando i tuoi progetti…</Trans>
      </p>
    );
  }
  if (projectCount === 0) {
    return (
      <p className="text-[14px] text-ink-2">
        <Trans>Non hai ancora progetti. Partiamo da un template, oppure uno vuoto.</Trans>
      </p>
    );
  }
  if (insights.length === 0) {
    return (
      <p className="text-[14px] text-ink-2">
        {projectCount === 1 ? (
          <Trans>Hai 1 progetto attivo. Tutto sotto controllo.</Trans>
        ) : (
          <Trans>Hai {projectCount} progetti attivi. Tutto sotto controllo.</Trans>
        )}
      </p>
    );
  }
  return (
    <p className="text-[14px] text-ink-2">
      {insights.length === 1 ? (
        <Trans>Ho notato una cosa che vale la pena guardare.</Trans>
      ) : (
        <Trans>Ho notato {insights.length} cose che valgono la pena.</Trans>
      )}
    </p>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// New project menu — kept as a small named component so the hero CTA
// row reads cleanly.
// ───────────────────────────────────────────────────────────────────────────

const NewProjectMenu: React.FC<{
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onNewBlank: () => void;
  readonly onNewFromTemplate: () => void;
}> = ({ open, onOpenChange, onNewBlank, onNewFromTemplate }) => {
  const { i18n } = useLingui();
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={i18n._(msg`Crea nuovo progetto`)}
          className="rounded-full"
        >
          <FolderPlus className="h-3.5 w-3.5" aria-hidden />
          <Trans>Nuovo</Trans>
          <ChevronDown className="h-3 w-3" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-64 p-1">
        <button
          type="button"
          onClick={onNewFromTemplate}
          className={cn(
            "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left",
            "text-ink-1 hover:bg-ink-1/[0.04]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          )}
        >
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ando-orange" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block font-medium text-[12.5px]">
              <Trans>Da template</Trans>
            </span>
            <span className="mt-0.5 block text-[11px] text-ink-3">
              <Trans>Persona, capability e prima impostazione già pronti per ruolo.</Trans>
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={onNewBlank}
          className={cn(
            "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left",
            "text-ink-1 hover:bg-ink-1/[0.04]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          )}
        >
          <FilePlus2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block font-medium text-[12.5px]">
              <Trans>Progetto vuoto</Trans>
            </span>
            <span className="mt-0.5 block text-[11px] text-ink-3">
              <Trans>Parti da zero — solo nome, icona e colore.</Trans>
            </span>
          </span>
        </button>
      </PopoverContent>
    </Popover>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Empty hero — narrative cold-start state, NOT the generic SurfaceEmptyState
// ───────────────────────────────────────────────────────────────────────────

const EmptyHero: React.FC<{
  readonly onTemplate: () => void;
  readonly onBlank: () => void;
}> = ({ onTemplate, onBlank }) => (
  <div className="flex flex-col items-start gap-4 rounded-2xl border border-hairline/60 bg-cream/40 p-8">
    <span aria-hidden className="block h-12 w-12 shrink-0">
      <LoginBlob size="mini" />
    </span>
    <div className="space-y-2">
      <h2 className="font-semibold text-[18px] text-ink-1">
        <Trans>Il tuo primo spazio di lavoro</Trans>
      </h2>
      <p className="max-w-md text-[13px] text-ink-2 leading-relaxed">
        <Trans>
          I progetti raggruppano conversazioni, conoscenza e automazioni per cliente o area di
          lavoro. Posso ricordare cosa hai fatto la volta scorsa e ripartire da lì.
        </Trans>
      </p>
    </div>
    <div className="flex items-center gap-2">
      <Button variant="primary" size="sm" onClick={onTemplate}>
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        <Trans>Parti da template</Trans>
      </Button>
      <Button variant="ghost" size="sm" onClick={onBlank}>
        <FolderPlus className="h-3.5 w-3.5" aria-hidden />
        <Trans>Progetto vuoto</Trans>
      </Button>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// ProjectRow — dense pencil list row (scalable collection → list, not grid)
// ───────────────────────────────────────────────────────────────────────────

interface ProjectRowProps {
  readonly project: Project;
  readonly index: number;
  readonly onOpen: () => void;
  readonly onArchive: () => void;
  readonly archivePending: boolean;
}

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  index,
  onOpen,
  onArchive,
  archivePending,
}) => {
  const { i18n } = useLingui();
  const activity = classifyActivity(project.last_active_at);
  const lastConvTitle = (project.last_conversation_title ?? "").trim();
  const lastAt = formatShortRelative(project.last_active_at);
  const overdueCount = project.overdue_tasks_count ?? 0;
  const nextRunAt = project.next_run_at ? formatShortUntil(project.next_run_at) : null;
  const memoryCount = project.memory_count ?? 0;

  // Story line — the one the user reads first. We prefer the last
  // conversation title (most recent conscious action), fall back to
  // the last artifact, then to a calm "nuovo spazio" copy.
  let storyLine: React.ReactNode;
  if (lastConvTitle.length > 0) {
    storyLine = <span className="text-ink-2 italic">"{lastConvTitle}"</span>;
  } else if (project.last_artifact_filename) {
    storyLine = <span className="text-ink-2 italic">{project.last_artifact_filename}</span>;
  } else if ((project.description ?? "").trim().length > 0) {
    storyLine = <span className="text-ink-2">{project.description}</span>;
  } else {
    storyLine = (
      <span className="text-ink-3 italic">
        <Trans>nuovo spazio, nessuna attività ancora</Trans>
      </span>
    );
  }

  const hasMeta = overdueCount > 0 || !!nextRunAt || memoryCount > 0;

  return (
    <ListItemRow
      itemId={project.project_id}
      index={index}
      onClick={onOpen}
      actionLabel={i18n._(msg`Apri progetto: ${project.name}`)}
      avatar={
        <ProjectIcon
          iconName={project.icon}
          colorToken={project.color}
          size={13}
          className="mt-0.5 h-7 w-7 shrink-0"
        />
      }
      eyebrow={
        <span
          aria-hidden
          title={activity}
          className={cn("h-1.5 w-1.5 shrink-0 rounded-full", ACTIVITY_DOT[activity])}
        />
      }
      trailing={
        lastAt.length > 0 ? (
          <span className="text-[10px] text-ink-3 tabular-nums">{lastAt}</span>
        ) : null
      }
      title={project.name}
      preview={storyLine}
      pills={
        hasMeta ? (
          <MetaRow className="gap-x-3">
            {overdueCount > 0 ? (
              <MetaItem icon={<Timer size={12} strokeWidth={1.75} />} className="text-ando-orange">
                <Trans>{overdueCount} in ritardo</Trans>
              </MetaItem>
            ) : null}
            {nextRunAt ? (
              <MetaItem icon={<CalendarClock size={12} strokeWidth={1.75} />}>{nextRunAt}</MetaItem>
            ) : null}
            {memoryCount > 0 && overdueCount === 0 && !nextRunAt ? (
              <MetaItem icon={<History size={12} strokeWidth={1.75} />}>
                <Trans>{memoryCount} ricordi</Trans>
              </MetaItem>
            ) : null}
          </MetaRow>
        ) : null
      }
      actions={
        <ListItemActionButton
          label={i18n._(msg`Archivia progetto ${project.name}`)}
          icon={
            archivePending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Archive className="h-3.5 w-3.5" aria-hidden />
            )
          }
          onClick={() => {
            if (!archivePending) onArchive();
          }}
        />
      }
    />
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ───────────────────────────────────────────────────────────────────────────

const ProjectHubSkeleton: React.FC = () => (
  <ul className="flex flex-col" aria-hidden>
    {Array.from({ length: 6 }).map((_, i) => (
      <li
        key={i}
        className="flex animate-pulse items-start gap-3 border-hairline/60 border-b px-2 py-3.5 last:border-b-0"
      >
        <div className="mt-0.5 h-7 w-7 shrink-0 rounded-md bg-ink-1/[0.06]" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-1/3 rounded bg-ink-1/[0.06]" />
          <div className="h-2.5 w-2/3 rounded bg-ink-1/[0.04]" />
        </div>
      </li>
    ))}
  </ul>
);
